import React, { useCallback, useEffect, useState } from "react";
import { AutoComplete, Col, Row, Spin } from "antd";
import styled from "styled-components";
import { debounce } from "lodash";
import { getCusCorporate } from "../../../datasource/CustomerDatasource";
import { getShopDetail } from "../../../datasource/CreditLimitDatasource";
import { type ShopDetailEntity, ShopDetailEntity_INIT } from "../../../entities/CreditLimitEntity";
import { MainOrderCreditDetailTab } from "../../CreditLimitPage/DetailTabs/Main";
import { MainOrderCreditDetailTabNew } from "../../CreditLimitPage/DetailTabs/MainNew";

const StyledSelect = styled(AutoComplete)`
  .ant-select-selection-search-input,
  .ant-select-selection-placeholder,
  .ant-select-selection-item {
    font-family: Sarabun !important;
    font-size: 14px;
  }
`;
interface Props {
  temporaryCreditLimitId: string;
  onCallbackShop: (shop) => void;
  callBackCusId: (CusId) => void;
  cusCompId: string;
}

function DetailSection({
  temporaryCreditLimitId,
  onCallbackShop,
  cusCompId,
  callBackCusId,
}: Props) {
  const companyCode = JSON.parse(localStorage.getItem("company")!);
  const [loading, setLoading] = useState(false);
  const [shopList, setShopList] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pageNumber, setPageNumber] = useState(1);
  const [isShop, setIsShop] = useState(false);
  const [cusComId, setCusComId] = useState<number>(0);
  const [shopDetail, setShopDetail] = useState<ShopDetailEntity>(ShopDetailEntity_INIT);
  const fetchShopList = useCallback(
    async (pageNumber: number) => {
      if (loading || !hasMore) return;
      setLoading(true);
      try {
        const response = await getCusCorporate({
          page: pageNumber,
          take: 10,
          companyCode: companyCode.companyCode,
          sortField: "updateDate",
          sortDirection: "DESC",
          search: searchTerm,
          isActive: true,
          customerType: "DL",
        });

        const newData = response.data.map((item: any, index: number) => {
          return {
            label: `${item.customerName} (${item.customerNo})`,
            value: `${item.customerName} (${item.customerNo})`,
            key: item.customerCompanyId,
            taxNo: item.customer.taxNo,
            dealerCode: item.dealerCode,
            customerId: item.customerId,
          };
        });

        setShopList((prev) => {
          const existingKeys = new Set(prev.map((shop) => shop.key));
          const uniqueNewData = newData.filter((shop) => !existingKeys.has(shop.key));
          return [...prev, ...uniqueNewData];
        });

        setHasMore(newData.length === 10);
      } catch (error) {
        console.error("Error fetching shop list:", error);
      } finally {
        setLoading(false);
      }
    },
    [loading, hasMore, companyCode.companyCode, searchTerm],
  );

  useEffect(() => {
    fetchShopList(1);
    setShopList([]);
  }, [searchTerm]);

  const ShopDetail = async (id) => {
    setLoading(true);
    try {
      await getShopDetail(id).then((res) => {
        setShopDetail(res);
        callBackCusId(res.customerId);
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cusCompId) {
      ShopDetail(cusCompId);
    } else if (cusComId !== 0) {
      ShopDetail(cusComId);
    }
  }, [cusComId, cusCompId]);

  const handleSearch = useCallback(
    debounce((value) => {
      setSearchTerm(value);
      setPageNumber(1);
      setHasMore(true);
    }, 50),
    [],
  );

  return (
    <>
      <div>
        {!temporaryCreditLimitId && (
          <Row gutter={[16, 16]} style={{ marginTop: 12 }}>
            <Col span={24}>
              <StyledSelect
                allowClear
                options={shopList}
                style={{ width: "100%", height: "40px" }}
                placeholder='ค้นหารหัสร้านค้า / ชื่อร้านค้า / เลขนิติบุคคล'
                filterOption={(input, option) =>
                  option?.label.toLowerCase().includes(input.toLowerCase())
                }
                onPopupScroll={(e) => {
                  const target = e.target as HTMLElement;
                  if (
                    target.scrollTop + target.clientHeight >= target.scrollHeight - 20 &&
                    hasMore &&
                    !loading
                  ) {
                    fetchShopList(pageNumber + 1);
                    setPageNumber((prev) => prev + 1);
                  }
                }}
                notFoundContent={loading ? <Spin size='small' /> : "ไม่มีข้อมูล"}
                onSearch={handleSearch}
                onSelect={(value, option: any) => {
                  setIsShop(true);
                  setCusComId(option.key.split("-")[0]);
                  onCallbackShop({
                    customerCompanyId: option.key.split("-")[0],
                    dealerName: option.label,
                    customerId: option.customerId,
                    dealerCode: option.dealerCode,
                    dealerNo: option.taxNo,
                  });
                }}
                onChange={(value) => {
                  if (!value) {
                    setIsShop(false);
                    onCallbackShop(undefined);
                  }
                }}
              />
            </Col>
          </Row>
        )}
      </div>
      {(isShop || cusCompId || temporaryCreditLimitId) && (
        <Spin spinning={loading} size='default'>
          <div style={{ marginTop: "20px" }}>
            <MainOrderCreditDetailTabNew cusComId={shopDetail?.customerCompanyId} sync={false} />
          </div>
        </Spin>
      )}
    </>
  );
}

export default DetailSection;
